<script setup>
import { useForm } from '@inertiajs/vue3'
import { useConfirm } from 'primevue/useconfirm'
import { useToast } from 'primevue/usetoast'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps({
  user: { type: Object, required: true },
  guards: { type: Object, default: () => ({ is_self: false, is_last_admin: false }) },
  roles: { type: Array, default: () => [] },
})

const { t } = useI18n()
const toast = useToast()
const confirm = useConfirm()

const currentRoleName = computed(() => {
  if (!props.user.roles || props.user.roles.length === 0) {
    return null
  }
  return props.user.roles[0].name
})

const form = useForm({
  role: currentRoleName.value,
})

const saveDisabled = computed(() => {
  if (props.guards.is_self) return true
  if (props.guards.is_last_admin && form.role !== 'admin') return true
  return false
})

const guardHint = computed(() => {
  if (props.guards.is_self) return 'messages.admin.user.cannot_change_own_role_hint'
  if (props.guards.is_last_admin) return 'messages.admin.user.cannot_demote_last_admin_hint'
  return null
})

const submit = () => {
  confirm.require({
    message: t('messages.admin.user.change_role_warning'),
    header: t('messages.admin.user.tabs.role'),
    accept: () => {
      form.put(route('admin.user.role', props.user.id), {
        preserveScroll: true,
        onSuccess: () => {
          toast.add({
            severity: 'success',
            summary: t('messages.updated'),
            detail: t('messages.admin.user.role_updated_success'),
            life: 3000,
          })
        },
      })
    },
  })
}
</script>

<template>
  <Card>
    <template #title>{{ $t('messages.admin.user.tabs.role') }}</template>
    <template #content>
      <div class="mb-3">
        <div class="text-sm text-gray-600 mb-1">{{ $t('messages.admin.user.current_roles') }}</div>
        <div class="flex flex-wrap gap-1">
          <Tag v-for="role in user.roles_display" :key="role" :value="role" severity="info" />
          <span v-if="!user.roles_display?.length" class="text-sm text-gray-400 italic">
            {{ $t('messages.admin.user.no_role') }}
          </span>
        </div>
      </div>
      <Fluid>
        <form class="flex flex-col gap-2" @submit.prevent="submit">
          <div class="flex items-end gap-2">
            <div class="flex-1">
              <label class="text-sm text-gray-600">{{ $t('messages.admin.select.role') }}</label>
              <Select
                v-model="form.role"
                :options="roles"
                optionLabel="label"
                optionValue="value"
                :disabled="guards.is_self"
              />
            </div>
            <Button type="submit" :disabled="form.processing || !form.role || saveDisabled" :loading="form.processing" class="w-auto!">
              {{ $t('messages.save') }}
            </Button>
          </div>
          <div v-if="guardHint" class="text-xs text-gray-400 italic">
            {{ $t(guardHint) }}
          </div>
        </form>
      </Fluid>
    </template>
  </Card>
</template>
